import { Box, Divider, useTheme } from "@mui/material";
import { Link } from "react-router";

import {
  RobotoStyledTab,
  RobotoStyledTabs,
  RobotoTooltip,
} from "@/shared/components";

import {
  TRIGGER_DETAIL_TAB_GROUPS,
  TRIGGER_DETAIL_TAB_LABELS,
  triggerDetailPath,
  type TriggerDetailTab,
  type TriggerDetailTabName,
} from "./triggerDetailTabs";

interface TriggerDetailTabBarProps {
  triggerName: string;
  tabs: readonly TriggerDetailTab[];
  activeTab: TriggerDetailTabName;
}

/**
 * Tab strip for the trigger detail pages.
 *
 * Every tab is a `Link` to its own URL and the active one is passed in from
 * the route, so the strip holds no state and a tab is shareable and
 * bookmarkable. The strip is two groups with a rule between them: the
 * trigger and its history, then what its runs produced elsewhere. A tab
 * with nothing to show stays in place, greyed, with the reason on hover, and
 * after the group's live tabs so the strip never alternates.
 */
export function TriggerDetailTabBar({
  triggerName,
  tabs,
  activeTab,
}: TriggerDetailTabBarProps) {
  const theme = useTheme();
  const byName = new Map(tabs.map((tab) => [tab.name, tab]));
  const isGreyed = (name: TriggerDetailTabName) =>
    byName.get(name)?.unavailableReason !== null;

  return (
    <Box sx={{ paddingTop: theme.spacing(0.5) }}>
      <RobotoStyledTabs value={activeTab} aria-label="trigger detail tabs">
        {TRIGGER_DETAIL_TAB_GROUPS.flatMap((group, groupIndex) => [
          ...(groupIndex > 0
            ? [<TabGroupRule key={`rule-${groupIndex}`} />]
            : []),
          ...[...group]
            .sort((a, b) => Number(isGreyed(a)) - Number(isGreyed(b)))
            .map((name) => {
              const reason = byName.get(name)?.unavailableReason ?? null;
              return reason === null ? (
                <RobotoStyledTab
                  key={name}
                  value={name}
                  label={TRIGGER_DETAIL_TAB_LABELS[name]}
                  component={Link}
                  to={triggerDetailPath(triggerName, name)}
                  replace
                />
              ) : (
                <UnavailableTab
                  key={name}
                  value={name}
                  label={TRIGGER_DETAIL_TAB_LABELS[name]}
                  reason={reason}
                />
              );
            }),
        ])}
      </RobotoStyledTabs>
      <Divider sx={{ borderColor: theme.border.color }} />
    </Box>
  );
}

/**
 * MUI `Tabs` clones every child with its own props (`value`, `selected`,
 * `onChange`, …) and locates the indicator by the child's position among the
 * strip's DOM children, so each custom child must accept those props and
 * render exactly one element.
 */
type InjectedTabProps = Record<string, unknown>;

function TabGroupRule(_props: InjectedTabProps) {
  const theme = useTheme();
  return (
    <Divider
      orientation="vertical"
      flexItem
      role="separator"
      sx={{
        alignSelf: "center",
        height: theme.spacing(2.5),
        marginX: theme.spacing(1),
        borderColor: theme.border.color,
      }}
    />
  );
}

function UnavailableTab({
  reason,
  label,
  value,
  ...injected
}: InjectedTabProps & { label: string; value: string; reason: string }) {
  // A disabled tab takes no pointer events, so the wrapper carries the
  // hover; it stretches like its siblings so the label sits on their line.
  return (
    <RobotoTooltip title={reason} placement="bottom">
      <span style={{ display: "inline-flex", alignSelf: "stretch" }}>
        <RobotoStyledTab {...injected} value={value} label={label} disabled />
      </span>
    </RobotoTooltip>
  );
}
